import type { Id } from "@supermanus/document-model";
import { useEffect, useRef, useState } from "react";

export type SceneTransferEpisodeOption = { id: Id; label: string };
export type SceneTransferProductionOption = { id: Id; title: string };

const CURRENT_PRODUCTION = "";

function pickDestId(
  list: SceneTransferEpisodeOption[],
  prev: string,
): string {
  if (prev && list.some((e) => e.id === prev)) return prev;
  return list[0]?.id ?? "";
}

export function SceneTransferDialog({
  sceneLabel,
  currentProductionTitle,
  episodesInCurrent,
  otherProductions,
  canMove,
  onLoadProductionEpisodes,
  onCopy,
  onMove,
  onCancel,
}: {
  sceneLabel: string;
  currentProductionTitle: string;
  /** Other episodes in the open production. */
  episodesInCurrent: SceneTransferEpisodeOption[];
  otherProductions: SceneTransferProductionOption[];
  canMove: boolean;
  onLoadProductionEpisodes?: (
    productionId: Id,
  ) => Promise<SceneTransferEpisodeOption[]>;
  onCopy: (dest: { productionId: Id | null; episodeId: Id }) => void;
  onMove: (dest: { productionId: Id | null; episodeId: Id }) => void;
  onCancel: () => void;
}) {
  // Snapshot production list when the dialog mounts so parent re-renders
  // (presence/sync) cannot reshuffle or briefly empty the <select> options —
  // that makes the controlled value fall back to the first option.
  const [frozenProductions] = useState(otherProductions);
  const hasOtherProductions = frozenProductions.length > 0;

  const [productionKey, setProductionKey] = useState(
    episodesInCurrent.length > 0
      ? CURRENT_PRODUCTION
      : (frozenProductions[0]?.id ?? CURRENT_PRODUCTION),
  );
  const [episodes, setEpisodes] = useState<SceneTransferEpisodeOption[]>(
    () =>
      productionKey === CURRENT_PRODUCTION ? episodesInCurrent : [],
  );
  const [destId, setDestId] = useState(episodesInCurrent[0]?.id ?? "");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Parent often passes a fresh episodesInCurrent / callback every render.
  // Keep them in refs so the load effect only re-runs when productionKey changes.
  const episodesInCurrentRef = useRef(episodesInCurrent);
  episodesInCurrentRef.current = episodesInCurrent;
  const loadEpisodesRef = useRef(onLoadProductionEpisodes);
  loadEpisodesRef.current = onLoadProductionEpisodes;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const loadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancelRef.current();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadError(null);
      if (productionKey === CURRENT_PRODUCTION) {
        const list = episodesInCurrentRef.current;
        setEpisodes(list);
        setDestId((prev) => pickDestId(list, prev));
        setLoading(false);
        loadedKeyRef.current = CURRENT_PRODUCTION;
        return;
      }
      const loader = loadEpisodesRef.current;
      if (!loader) {
        setEpisodes([]);
        setDestId("");
        setLoadError("Kan ikke indlæse episoder i andre produktioner.");
        return;
      }
      // Already loaded for this production — skip re-fetch/blink.
      if (loadedKeyRef.current === productionKey) return;

      setLoading(true);
      try {
        const list = await loader(productionKey);
        if (cancelled) return;
        setEpisodes(list);
        setDestId((prev) => pickDestId(list, prev));
        loadedKeyRef.current = productionKey;
      } catch {
        if (cancelled) return;
        setEpisodes([]);
        setDestId("");
        setLoadError("Kunne ikke indlæse episoder.");
        loadedKeyRef.current = null;
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [productionKey]);

  // Keep "current production" episode list in sync without resetting selection
  // or re-triggering the remote load path.
  useEffect(() => {
    if (productionKey !== CURRENT_PRODUCTION) return;
    setEpisodes(episodesInCurrent);
    setDestId((prev) => pickDestId(episodesInCurrent, prev));
  }, [productionKey, episodesInCurrent]);

  const destReady = Boolean(destId) && !loading;
  const productionId =
    productionKey === CURRENT_PRODUCTION ? null : productionKey;

  return (
    <div className="sm-dialog-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="sm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-scene-transfer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sm-scene-transfer-title">Kopiér eller flyt scene</h2>
        <p>
          {sceneLabel} kan kopieres eller flyttes til en anden episode — også i
          en anden produktion. Kopien lægges sidst i den valgte episode.
        </p>
        {episodesInCurrent.length === 0 && !hasOtherProductions ? (
          <p>Der er ingen andre episoder eller produktioner at vælge.</p>
        ) : (
          <>
            {hasOtherProductions ? (
              <label className="sm-dialog-field">
                Produktion
                <select
                  value={productionKey}
                  onChange={(e) => {
                    loadedKeyRef.current = null;
                    setProductionKey(e.target.value);
                  }}
                >
                  {episodesInCurrent.length > 0 ? (
                    <option value={CURRENT_PRODUCTION}>
                      {currentProductionTitle.trim() || "Denne produktion"}
                    </option>
                  ) : null}
                  {frozenProductions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title.trim() || "Unavngiven produktion"}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="sm-dialog-field">
              Episode
              <select
                value={destId}
                disabled={loading || episodes.length === 0}
                onChange={(e) => setDestId(e.target.value)}
              >
                {loading ? (
                  <option value="">Henter episoder…</option>
                ) : episodes.length === 0 ? (
                  <option value="">Ingen episoder</option>
                ) : (
                  episodes.map((ep) => (
                    <option key={ep.id} value={ep.id}>
                      {ep.label}
                    </option>
                  ))
                )}
              </select>
            </label>
            {loadError ? <p className="sm-dialog-hint">{loadError}</p> : null}
          </>
        )}
        <div className="sm-dialog-actions">
          <button type="button" onClick={onCancel}>
            Annuller
          </button>
          <button
            type="button"
            disabled={!destReady}
            onClick={() =>
              destReady &&
              onCopy({ productionId, episodeId: destId })
            }
          >
            Kopiér
          </button>
          <button
            type="button"
            className="sm-dialog-primary"
            disabled={!destReady || !canMove}
            title={
              canMove
                ? "Flyt scenen til den valgte episode"
                : "Der skal være mindst én scene tilbage i episoden"
            }
            onClick={() =>
              destReady &&
              canMove &&
              onMove({ productionId, episodeId: destId })
            }
          >
            Flyt
          </button>
        </div>
      </div>
    </div>
  );
}
